import { useEffect, useRef, useState } from "react";
import { Maximize2, Minimize2 } from "lucide-react";
import { fmtLagos, fmtUtc, STATE_LABEL, STATUS_LABEL } from "@/lib/format";
import { cn } from "@/lib/utils";

const FRESH = {
  OK: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  DELAYED: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  PARTIAL: "bg-sky-500/15 text-sky-300 border-sky-500/30",
  UNAVAILABLE: "bg-slate-500/20 text-slate-300 border-slate-500/40",
};
const DOT = { OK: "bg-emerald-400", DELAYED: "bg-amber-400", PARTIAL: "bg-sky-400", UNAVAILABLE: "bg-slate-400" };

export function FreshnessBadge({ freshness, testId }) {
  const s = freshness?.status || "UNAVAILABLE";
  return (
    <span data-testid={testId} className={cn("inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 font-mono text-[11px] uppercase tracking-wider", FRESH[s])}>
      <span className={cn("h-1.5 w-1.5 rounded-full", DOT[s], s === "OK" && "animate-pulse")} />
      {s}
      {freshness?.lag_minutes != null && <span className="opacity-70">{freshness.lag_minutes}m</span>}
    </span>
  );
}

export function Timestamp({ ts, withDate = true, className, testId }) {
  if (!ts) return <span className={cn("font-mono text-xs text-slate-500", className)}>—</span>;
  return (
    <span data-testid={testId} className={cn("font-mono text-xs text-slate-300", className)} title={ts}>
      {fmtUtc(ts, withDate)} <span className="text-slate-500">· {fmtLagos(ts)}</span>
    </span>
  );
}

const STATE_STYLE = {
  DETECTED: "bg-lime-500/15 text-lime-300 border-lime-500/30",
  TRIAGED: "bg-sky-500/15 text-sky-300 border-sky-500/30",
  UNDER_REVIEW: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  VERIFIED: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  NOT_VERIFIED: "bg-slate-500/20 text-slate-300 border-slate-500/40",
  INCONCLUSIVE: "bg-violet-500/15 text-violet-300 border-violet-500/30",
  CLOSED: "bg-slate-700/40 text-slate-400 border-slate-600/40",
};

export function StateBadge({ state, testId }) {
  return (
    <span data-testid={testId} className={cn("inline-flex rounded border px-2 py-0.5 font-mono text-[11px] uppercase tracking-wider", STATE_STYLE[state] || STATE_STYLE.CLOSED)}>
      {STATE_LABEL[state] || state}
    </span>
  );
}

const RUN_STYLE = {
  PROVISIONAL_SHIP_MATCH: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  MULTIPLE_CANDIDATES: "bg-amber-500/10 text-amber-200 border-amber-500/30",
  NON_SHIP_LIKELY: "bg-sky-500/15 text-sky-300 border-sky-500/30",
  INCONCLUSIVE: "bg-violet-500/15 text-violet-300 border-violet-500/30",
  PENDING: "bg-slate-500/20 text-slate-300 border-slate-500/40",
};

export function RunStatusBadge({ status, testId }) {
  const key = status || "PENDING";
  return (
    <span data-testid={testId} className={cn("inline-flex rounded border px-2 py-0.5 font-mono text-[11px] uppercase tracking-wider", RUN_STYLE[key] || RUN_STYLE.PENDING)}>
      {STATUS_LABEL[key] || key}
    </span>
  );
}

const GRADE_STYLE = {
  STRONG: "bg-amber-400 text-slate-950",
  MODERATE: "bg-amber-500/25 text-amber-200 border border-amber-500/40",
  LOW: "bg-slate-600/40 text-slate-200 border border-slate-500/40",
  INDETERMINATE: "bg-slate-800 text-slate-400 border border-slate-700",
};

export function GradeBadge({ grade, testId }) {
  return <span data-testid={testId} className={cn("rounded px-2 py-0.5 font-mono text-[11px] font-semibold uppercase tracking-wider", GRADE_STYLE[grade] || GRADE_STYLE.INDETERMINATE)}>{grade}</span>;
}

const QC_STYLE = { VALID: "text-emerald-300", SUSPECT: "text-amber-300", INVALID: "text-red-400", MISSING: "text-slate-500" };
export function QcFlag({ flag }) {
  return <span className={cn("font-mono text-[11px] uppercase", QC_STYLE[flag] || "text-slate-400")}>{flag}</span>;
}

function FullscreenToggle({ target, testId }) {
  const [full, setFull] = useState(false);
  useEffect(() => {
    const f = () => setFull(!!target.current && document.fullscreenElement === target.current);
    document.addEventListener("fullscreenchange", f);
    return () => document.removeEventListener("fullscreenchange", f);
  }, [target]);
  const Icon = full ? Minimize2 : Maximize2;
  return (
    <button data-testid={testId ? `${testId}-fullscreen` : undefined} title={full ? "Exit full screen" : "Full screen"} onClick={() => (document.fullscreenElement ? document.exitFullscreen() : target.current?.requestFullscreen())}
      className="rounded p-1 text-slate-500 transition-colors hover:bg-white/5 hover:text-lime-300"><Icon className="h-3.5 w-3.5" /></button>
  );
}

export function Panel({ title, subtitle, actions, children, className, testId }) {
  const ref = useRef(null);
  return (
    <section ref={ref} data-testid={testId} className={cn("panel flex flex-col", className)}>
      {(title || actions) && (
        <header className="flex items-start justify-between gap-3 border-b border-[#1E3E30] px-4 py-3">
          <div>
            {title && <h3 className="font-display text-sm font-semibold tracking-[0.08em] text-slate-300">{title}</h3>}
            {subtitle && <p className="mt-0.5 text-xs text-slate-500">{subtitle}</p>}
          </div>
          <div className="flex items-center gap-1">{actions}<FullscreenToggle target={ref} testId={testId} /></div>
        </header>
      )}
      <div className="flex-1 p-4">{children}</div>
    </section>
  );
}

export function EmptyState({ title, hint, testId }) {
  return (
    <div data-testid={testId} className="flex flex-col items-center justify-center rounded border border-dashed border-[#1E3E30] px-6 py-10 text-center">
      <p className="text-sm text-slate-300">{title}</p>
      {hint && <p className="mt-1 max-w-sm text-xs text-slate-500">{hint}</p>}
    </div>
  );
}

export function Loading({ label = "Loading…" }) {
  return (
    <div className="flex items-center gap-3 py-8 text-xs text-slate-500" data-testid="loading-indicator">
      <span className="h-3 w-3 animate-spin rounded-full border-2 border-lime-500/40 border-t-lime-400" />
      {label}
    </div>
  );
}

export function ErrorState({ message, testId }) {
  return (
    <div data-testid={testId || "error-state"} className="rounded border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
      {message}
    </div>
  );
}

export function KpiTile({ label, value, sub, accent = "cyan", testId }) {
  const accents = { cyan: "text-lime-300", amber: "text-amber-300", emerald: "text-emerald-300", slate: "text-slate-200", violet: "text-violet-300" };
  return (
    <div data-testid={testId} className="panel flex flex-col gap-1 px-4 py-3 transition-transform duration-200 hover:-translate-y-px">
      <span className="text-[11px] font-medium uppercase tracking-[0.14em] text-slate-500">{label}</span>
      <span className={cn("font-display text-3xl font-bold tabular-nums", accents[accent])}>{value}</span>
      {sub && <span className="text-xs text-slate-500">{sub}</span>}
    </div>
  );
}

export function Field({ label, children, hint }) {
  return (
    <label className="flex flex-col gap-1 text-xs text-slate-400">
      <span className="font-medium uppercase tracking-wider text-slate-500">{label}</span>
      {children}
      {hint && <span className="text-[11px] text-slate-600">{hint}</span>}
    </label>
  );
}

export const inputCls = "h-9 rounded border border-[#1E3E30] bg-[#11241C] px-3 text-sm text-slate-200 outline-none focus:border-lime-500/60 focus:ring-1 focus:ring-lime-500/40";
export const btnPrimary = "inline-flex h-9 items-center gap-2 rounded bg-lime-500 px-4 text-sm font-semibold text-slate-950 transition-colors hover:bg-lime-400 disabled:cursor-not-allowed disabled:opacity-50";
export const btnGhost = "inline-flex h-9 items-center gap-2 rounded border border-[#1E3E30] px-3 text-sm text-slate-300 transition-colors hover:border-lime-500/50 hover:text-lime-200 disabled:opacity-50";
export const btnAmber = "inline-flex h-9 items-center gap-2 rounded bg-amber-500 px-4 text-sm font-semibold text-slate-950 transition-colors hover:bg-amber-400 disabled:opacity-50";
